import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type UserRole } from '../api';
import { AuthShell } from '../components/AuthShell';
import { useAuthStore } from '../auth';

export function AcceptInvitePage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const setTokens = useAuthStore((s) => s.setTokens);
  const [invite, setInvite] = useState<{
    email: string;
    role: UserRole;
    org: { name: string; status: string };
  } | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .lookupInvitation(token)
      .then((data) => {
        if (cancelled) return;
        setInvite({
          email: data.email,
          role: data.role,
          org: { name: data.org.name, status: data.org.status },
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(
          e instanceof ApiError ? humanize(e.message) : 'failed_to_load',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.acceptInvitation(token, { name, password });
      // Auto-login with the freshly-created credentials.
      const tokens = await api.login({
        email: invite!.email,
        password,
      });
      setTokens(tokens);
      navigate('/');
    } catch (e) {
      setErr(
        e instanceof ApiError ? humanize(e.message) : 'failed_to_accept',
      );
    } finally {
      setBusy(false);
    }
  }

  if (err && !invite) {
    return (
      <AuthShell title="Invitation problem">
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {err}
        </div>
      </AuthShell>
    );
  }
  if (!invite) {
    return <AuthShell title="Loading…"><p className="text-muted">Checking your invitation.</p></AuthShell>;
  }

  return (
    <AuthShell
      title={`Join ${invite.org.name}`}
      subtitle={`You've been invited as ${invite.role.toLowerCase()}.`}
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label">Email</label>
          <input className="input" value={invite.email} readOnly disabled />
        </div>
        <div>
          <label className="label">Your name</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
            autoComplete="name"
          />
        </div>
        <div>
          <label className="label">Pick a password</label>
          <input
            className="input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
          <p className="mt-1 text-xs text-muted">Minimum 8 characters.</p>
        </div>
        {err && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {err}
          </div>
        )}
        <button className="btn btn-primary w-full" disabled={busy}>
          {busy ? 'Joining…' : 'Accept invitation →'}
        </button>
      </form>
    </AuthShell>
  );
}

function humanize(code: string): string {
  switch (code) {
    case 'invitation_not_found':
      return "This invitation link isn't recognized. Ask your inviter to resend.";
    case 'invitation_revoked':
      return 'This invitation has been revoked.';
    case 'invitation_already_accepted':
      return 'This invitation has already been used.';
    case 'invitation_expired':
      return 'This invitation has expired. Ask your inviter to send a new one.';
    case 'email_already_a_user':
      return 'A user with that email already exists.';
    default:
      return code;
  }
}
